import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Popconfirm, Row, Select, Skeleton, Table, notification } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { DELETE_PARTICIPANT, GET_PARTICIPANTS, GET_UNIQUE_PARTIES } from './PartyQueries';
import ParticipantForm from './PartyForm';
import { useIsAuthenticated } from '@azure/msal-react';

const ParticipantList: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { proceedingId } = useParams<any>();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [participantId, setParticipantId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [selectedParty, setSelectedParty] = useState('');

  const [deleteParticipant, { loading: deleting, data: deleteResult, error: deleteError }] =
    useMutation(DELETE_PARTICIPANT);

  const [getParticipants, { data: response, loading, error, refetch }] =
    useLazyQuery(GET_PARTICIPANTS);

  const [getUniqueParties, { data: uniqueParties, refetch: refetcUniqueParties }] =
    useLazyQuery(GET_UNIQUE_PARTIES);
  const getFormTitle = () => {
    if (isAuthenticated) {
      return (participantId !== null ? 'Edit' : 'Add') + ' Party';
    }
    return 'View Party Details';
  };

  const deleteParticipantFromProceeding = async (participant: any) => {
    try {
      const result = await deleteParticipant({
        variables: {
          input: {
            proceedingNumber: participant.proceedingId,
            participantId: participant.id,
          },
        },
      });

      if (result.data.deleteParticipant.result === 'Error') {
        throw new Error(result.data.deleteParticipant.errorMessage);
      }

      notification.success({
        message: 'Success',
        description: 'Party has been deleted.',
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.message || 'An error occurred while trying to delete the party.',
      });
    }
  };

  useEffect(() => {
  if (deleteResult || deleteError) {
    refetch();
  }
  }, [deleteResult, deleteError]);

  const editViewParticipant = (participant: any) => {
    if (!isAuthenticated) return;
    setParticipantId(participant.id);
    handleModalOpen();
  };
  useEffect(() => {
    if (tab === '3' && proceedingId) {
      getParticipants({
        variables: {
          input: { proceedingNumber: proceedingId },
        },
      });
    }
  }, [tab]);
  const participants = response?.participants?.data.map((participant: any) => ({
    ...participant,
    type:
      participant.type === 'Other' ? participant.otherParticipantTypeDescription : participant.type,
  }));

  const columns: any[] = [
    {
      title: 'Name',
      dataIndex: 'partyName',
      key: 'partyName',
      width: 280,
    },
  ];
  if (isAuthenticated) {
    columns.push(
      {
        title: 'Request Received Date',
        dataIndex: 'receiptRequestDate',
        key: 'receiptRequestDate',
        width: 80,
      },
      {
        title: 'Party Type',
        dataIndex: 'type',
        key: 'type',
        width: 280,
      },
      {
        title: 'Comments',
        dataIndex: 'comments',
        key: 'comments',
        width: 280,
      },
      {
        title: 'Delete',
        key: 'deleteIcon',
        width: 50,
        onCell: () => {
          return {
            onClick: (event: any) => {
              event.stopPropagation();
            },
          };
        },
        render: (_: any, participant: any) => {
          return (
            <>
              <div className="ml-1 inline-block">
                <Popconfirm
                  title="Delete Party"
                  description={'Are you sure to delete this party?'}
                  onConfirm={() => deleteParticipantFromProceeding(participant)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    icon={<DeleteOutlined />}
                    size={'small'}
                    style={{ backgroundColor: 'white', color: 'red' }}
                  />
                </Popconfirm>
              </div>
            </>
          );
        },
      }
    );
  }

  const handleModalOpen = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setParticipantId(null);
    refetch();
  };
  const handleParticipantFormSubmit = () => {
    setParticipantId(null);
    refetch();
    setIsModalVisible(false);
  };

  return (
    <>
      {loading ? (
        <Skeleton />
      ) : (
        <div style={{ marginBottom: '10px', minHeight: '100vh', marginTop: '10px' }}>
          <Row>
            {isAuthenticated && (
              <Button type="primary" onClick={handleModalOpen} style={{ marginLeft: '10px' }}>
                Add Party
              </Button>
            )}
          </Row>
          <br />
          <Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                flexGrow: 10,
              }}
            >
              <Card title={<span className="highlighted-title">List of Parties and Non-Participants</span>} className="card">
                <Table
                  dataSource={participants}
                  loading={loading}
                  columns={columns}
                  onRow={(participant: any) => ({
                    onClick: () => {
                      editViewParticipant(participant);
                    },
                    style: { cursor: 'pointer' },
                  })}
                  rowClassName="clickable-row"
                />
              </Card>
            </div>
          </Row>
          <Modal
            title={getFormTitle()}
            open={isModalVisible}
            onCancel={handleModalClose}
            footer={null}
            width={800}
            destroyOnClose
          >
            <ParticipantForm
              onSubmit={handleParticipantFormSubmit}
              onCancel={handleModalClose}
              proceedingId={proceedingId}
              participantId={participantId}
              selectedPartyBaId={selectedParty}
            />
          </Modal>
        </div>
      )}
    </>
  );
};

export default ParticipantList;
